import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import MaInput from "../../../../Components/UI/ma-input";
import { MenuItem } from "@mui/material";
import { GET_SELLER_TRENDS_ANALYTICS } from "../../../../Utils/Queries";

const COLORS = {
  revenue: {
    line: "#8979FF",
    halo: "#8979FF4D",
  },
  order: {
    line: "#FF928A",
    halo: "#FF928A66",
  },
};

const GRID_STROKE = "#E8EAED";
const AXIS_TICK = "#9CA3AF";
const BASELINE = "#D8DCE3";

/** Soft outer ring + solid core dot (unicorn / soft-UI style) */
function HaloDot({ cx, cy, color, halo, rCore = 4, rHalo = 13 }) {
  if (cx == null || cy == null) return null;
  return (
    <g className="recharts-dot recharts-area-dot">
      <circle cx={cx} cy={cy} r={rHalo} fill={halo} stroke="none" />
      <circle cx={cx} cy={cy} r={rCore} fill={color} stroke="#fff" strokeWidth={1.5} />
    </g>
  );
}

function RevenueDot(props) {
  return (
    <HaloDot
      {...props}
      color={COLORS.revenue.line}
      halo={COLORS.revenue.halo}
    />
  );
}

function OrderDot(props) {
  return (
    <HaloDot {...props} color={COLORS.order.line} halo={COLORS.order.halo} />
  );
}

function ActiveHaloDot({ cx, cy, color, halo }) {
  if (cx == null || cy == null) return null;
  return (
    <g>
      <circle cx={cx} cy={cy} r={16} fill={halo} stroke="none" />
      <circle cx={cx} cy={cy} r={5} fill={color} stroke="#fff" strokeWidth={2} />
    </g>
  );
}

/** Legend row: dashed segment + dot with halo, matching chart language */
function LegendGlyph({ color, halo }) {
  return (
    <svg width="40" height="18" viewBox="0 0 40 18" aria-hidden>
      <line
        x1="2"
        y1="9"
        x2="28"
        y2="9"
        stroke={color}
        strokeWidth="1.5"
        strokeDasharray="4 3"
        strokeLinecap="round"
        opacity={0.85}
      />
      <circle cx="20" cy="9" r="9" fill={halo} />
      <circle cx="20" cy="9" r="3.5" fill={color} stroke="#fff" strokeWidth="1.25" />
    </svg>
  );
}

function CustomLegend({ payload }) {
  if (!payload?.length) return null;
  return (
    <div
      className="flex justify-center items-center gap-8 pt-3"
      style={{ fontFamily: "inherit" }}
    >
      {payload.map((entry) => (
        <div
          key={entry.dataKey}
          className="flex items-center gap-2 text-[12px] text-[#6B7280] tracking-tight"
        >
          <LegendGlyph
            color={entry.color}
            halo={
              entry.dataKey === "revenue"
                ? COLORS.revenue.halo
                : COLORS.order.halo
            }
          />
          <span className="font-medium text-[#5C6370]">{entry.value}</span>
        </div>
      ))}
    </div>
  );
}

const REVENUE_DATA_FALLBACK = [
  { name: "Jan", revenue: 50, order: 95 },
  { name: "Feb", revenue: 72, order: 108 },
  { name: "Mar", revenue: 35, order: 55 },
  { name: "Apr", revenue: 30, order: 105 },
  { name: "May", revenue: 100, order: 188 },
  { name: "June", revenue: 47, order: 85 },
];

function monthCanonical(month) {
  const s = String(month).trim();
  const iso = s.match(/^(\d{4})-(\d{1,2})(?:-(\d{1,2}))?$/);
  if (iso) {
    return `${iso[1]}-${String(iso[2]).padStart(2, "0")}`;
  }
  const t = Date.parse(s);
  if (!Number.isNaN(t)) {
    const d = new Date(t);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  }
  const lower = s.toLowerCase().replace(/\s+/g, "");
  const fullNames = [
    "january",
    "february",
    "march",
    "april",
    "may",
    "june",
    "july",
    "august",
    "september",
    "october",
    "november",
    "december",
  ];
  for (let i = 0; i < fullNames.length; i++) {
    const name = fullNames[i];
    if (
      lower === name ||
      lower.startsWith(name) ||
      (lower.length >= 3 && name.startsWith(lower))
    ) {
      return `2000-${String(i + 1).padStart(2, "0")}`;
    }
  }
  return lower || "unknown";
}

function shortMonthFromCanonical(canonical) {
  const m = canonical.match(/^(\d{4})-(\d{2})$/);
  if (m) {
    const d = new Date(Number(m[1]), Number(m[2]) - 1, 1);
    return d.toLocaleString("en-US", { month: "short" });
  }
  return String(canonical).slice(0, 3).replace(/^./, (c) => c.toUpperCase());
}

function mergeTrendsSeries(orders, revenue) {
  const byKey = new Map();
  const bump = (month, patch) => {
    const key = monthCanonical(month);
    const prev = byKey.get(key) || {
      name: shortMonthFromCanonical(key),
      revenue: 0,
      order: 0,
    };
    byKey.set(key, { ...prev, ...patch, name: shortMonthFromCanonical(key) });
  };
  for (const row of revenue || []) {
    bump(row.month, {
      revenue: Number(row.total) || 0,
    });
  }
  for (const row of orders || []) {
    bump(row.month, {
      order: Number(row.total) || 0,
    });
  }
  const rows = [...byKey.entries()]
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([, v]) => ({ name: v.name, revenue: v.revenue, order: v.order }));
  return rows.length ? rows : null;
}

const tickStyle = { fontSize: 11, fill: AXIS_TICK, fontWeight: 500 };

export const RevenueChartCard = () => {
  const { data } = useQuery(GET_SELLER_TRENDS_ANALYTICS);
  const trend = data?.getSellerTrendsAnalytics;

  const chartData = useMemo(() => {
    const merged = mergeTrendsSeries(trend?.orders, trend?.revenue);
    return merged ?? REVENUE_DATA_FALLBACK;
  }, [trend]);

  const yDomainMax = useMemo(() => {
    const peak = Math.max(
      1,
      ...chartData.map((d) =>
        Math.max(Number(d.revenue) || 0, Number(d.order) || 0)
      )
    );
    return Math.max(200, Math.ceil((peak * 1.05) / 40) * 40);
  }, [chartData]);

  const yTicks = useMemo(
    () =>
      [0, 1, 2, 3, 4, 5].map((i) => Math.round((i * yDomainMax) / 5)),
    [yDomainMax]
  );

  return (
  <div className="bg-white rounded-[14px] border border-[#EFEFEF] p-5 h-[400px]">
    <div className="flex items-start justify-between mb-4">
      <div>
        <h3 className="text-[15px] font-semibold text-[#3D3D3D] decoration-[#7B408C] my-0">
          Revenue & Orders Trend
        </h3>
        <p className="text-[12.5px] text-[#626262] my-0 mt-2">
          Daily performance over selected period
        </p>
      </div>

      <div>
        <MaInput value="date" defaultValue="date" select>
          <MenuItem value="date">Date</MenuItem>
        </MaInput>
      </div>
    </div>

    <div className="w-full h-[320px]">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={chartData}
          margin={{ top: 12, right: 12, left: -6, bottom: 4 }}
        >
          <defs>
            <linearGradient id="revenueGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={COLORS.revenue.line} stopOpacity={0.45} />
              <stop offset="55%" stopColor={COLORS.revenue.line} stopOpacity={0.12} />
              <stop offset="100%" stopColor={COLORS.revenue.line} stopOpacity={0} />
            </linearGradient>
            <linearGradient id="orderGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={COLORS.order.line} stopOpacity={0.42} />
              <stop offset="55%" stopColor={COLORS.order.line} stopOpacity={0.1} />
              <stop offset="100%" stopColor={COLORS.order.line} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid
            strokeDasharray="4 4"
            vertical
            horizontal
            stroke={GRID_STROKE}
            strokeOpacity={0.9}
          />
          <XAxis
            dataKey="name"
            axisLine={{ stroke: BASELINE, strokeWidth: 1 }}
            tickLine={false}
            tick={tickStyle}
            dy={6}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            tick={tickStyle}
            domain={[0, yDomainMax]}
            ticks={yTicks}
            width={36}
          />
          <Tooltip
            cursor={{ stroke: GRID_STROKE, strokeWidth: 1, strokeDasharray: "4 4" }}
            contentStyle={{
              borderRadius: 10,
              border: "1px solid #E8EAED",
              boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
              fontSize: 12,
              padding: "10px 12px",
            }}
            labelStyle={{ color: "#6B7280", marginBottom: 4 }}
          />
          <Legend content={(legendProps) => <CustomLegend {...legendProps} />} />
          <Area
            type="natural"
            name="Revenue"
            dataKey="revenue"
            stroke={COLORS.revenue.line}
            strokeWidth={1.75}
            fill="url(#revenueGrad)"
            isAnimationActive
            dot={(dotProps) => <RevenueDot {...dotProps} />}
            activeDot={(dotProps) => (
              <ActiveHaloDot
                {...dotProps}
                color={COLORS.revenue.line}
                halo={COLORS.revenue.halo}
              />
            )}
          />
          <Area
            type="natural"
            name="Order"
            dataKey="order"
            stroke={COLORS.order.line}
            strokeWidth={1.75}
            fill="url(#orderGrad)"
            isAnimationActive
            dot={(dotProps) => <OrderDot {...dotProps} />}
            activeDot={(dotProps) => (
              <ActiveHaloDot
                {...dotProps}
                color={COLORS.order.line}
                halo={COLORS.order.halo}
              />
            )}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  </div>
  );
};
